import { useEffect, useMemo, useState, type ReactNode } from 'react'

import type { User } from 'firebase/auth'

import { AuthContext, type AuthStatus } from '@/auth/auth-context'
import { isActiveAdmin, signOut, watchAuthState } from '@/services/auth'

/// Порт `AuthGate` из Flutter-версии.
///
/// Вход в Firebase сам по себе ничего не даёт: доступ определяет документ в
/// коллекции `admins` с `active: true`. Пользователя без такого документа
/// сразу разлогиниваем — иначе он висел бы «вошедшим» с пустым экраном.
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    // Поколение защищает от гонки: пока проверяем одного пользователя, мог
    // прилететь следующий authState — тогда старый ответ применять нельзя.
    let generation = 0
    let disposed = false

    const unsubscribe = watchAuthState(async (nextUser) => {
      const current = ++generation

      if (!nextUser) {
        if (disposed) return
        setUser(null)
        setStatus('signed-out')
        return
      }

      setStatus('checking')

      let allowed = false
      try {
        allowed = await isActiveAdmin(nextUser.uid)
      } catch {
        // Правила Firestore закрывают чужие документы: отказ читаем как «нет
        // доступа», а не как повод показать пустую оболочку.
        allowed = false
      }

      if (disposed || current !== generation) return

      if (!allowed) {
        await signOut()
        if (disposed || current !== generation) return
        setUser(null)
        setStatus('signed-out')
        return
      }

      setUser(nextUser)
      setStatus('signed-in')
    })

    return () => {
      disposed = true
      unsubscribe()
    }
  }, [])

  const value = useMemo(() => ({ status, user }), [status, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
